import { create } from 'zustand';
import type { QueryClient } from '@tanstack/react-query';
import type { WsClient } from '@/realtime/ws';
import { useAuth } from '@/stores/auth';
import { serverClock } from '@/lib/clock';
import type { PublicUser } from '@/api/types';
import { newerFour, newerWatch, usKeys } from './api';
import type { FourView, UsView, WatchView } from './types';

/** A ring from the partner: "come to our room" (SPEC_V15 §3.4). */
export interface UsRing {
  from: PublicUser;
  to: 'call' | 'watch';
  at: number;
}

interface RingState {
  ring: UsRing | null;
  set: (r: UsRing | null) => void;
}

export const useUsRing = create<RingState>((set) => ({ ring: null, set: (ring) => set({ ring }) }));

/** Keep Me.us_mode in step with the space (the mode gate reads Me). */
export function syncMeMode(v: UsView | null | undefined): void {
  const { me, setMe } = useAuth.getState();
  if (!me) return;
  const on = !!v?.space && !!v.mode_on;
  // A space the server lets us load is one we have access to (a preview pair, too).
  const preview = !!me.us_preview || !!v?.space;
  if (!!me.us_mode !== on || !!me.us_preview !== preview) setMe({ ...me, us_mode: on, us_preview: preview });
}

/** Wire the us.* events into the caches (called from wireRealtime). */
export function wireUs(ws: WsClient, qc: QueryClient): (() => void)[] {
  return [
    ws.on('us.updated', (d) => {
      if (!d?.us) return;
      if (d.us.server_time) serverClock.sample(d.us.server_time);
      qc.setQueryData<UsView>(usKeys.us, d.us);
      syncMeMode(d.us);
      if (d.us.space?.theme?.id) void qc.invalidateQueries({ queryKey: usKeys.themes });
    }),
    ws.on('us.ended', () => {
      qc.setQueryData<UsView | undefined>(usKeys.us, (prev) => (prev ? { ...prev, space: null, mode_on: false } : prev));
      syncMeMode(null);
      void qc.invalidateQueries({ queryKey: usKeys.us });
      qc.removeQueries({ queryKey: usKeys.watch });
      qc.removeQueries({ queryKey: usKeys.four });
    }),
    ws.on('us.watch', (d) => {
      if (!d?.watch) return;
      if (d.server_time) serverClock.sample(d.server_time);
      qc.setQueryData<WatchView>(usKeys.watch, (prev) => newerWatch(prev, d));
    }),
    ws.on('us.four', (d) => {
      if (!d?.four) return;
      qc.setQueryData<FourView>(usKeys.four, (prev) => newerFour(prev, d));
    }),
    ws.on('us.ring', (d) => {
      if (!d?.from) return;
      useUsRing.getState().set({ from: d.from, to: d.to === 'watch' ? 'watch' : 'call', at: Date.now() });
    }),
    // Unfriending or blocking ends a space (the server cascades): refetch.
    ws.on('connection.removed', ({ connection_id }) => {
      const v = qc.getQueryData<UsView>(usKeys.us);
      if (v?.space?.connection_id === connection_id) {
        qc.setQueryData<UsView>(usKeys.us, { ...v, space: null, mode_on: false });
        syncMeMode(null);
        void qc.invalidateQueries({ queryKey: usKeys.us });
      }
    }),
  ];
}
